import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            // Anti-enumeration: backend always returns 200 unless the payload
            // is structurally broken. Surface the generic message and proceed
            // to the "check your inbox" screen regardless.
            toast.error(formatApiError(err, "Could not submit request"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="forgot-password-page">
            {/* Left: form */}
            <div className="flex items-center justify-center px-6 sm:px-12 py-10">
                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2.5 mb-12">
                        <div className="h-8 w-8 bg-slate-900 rounded-md flex items-center justify-center">
                            <span className="text-white font-display font-bold text-sm">R</span>
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight text-slate-900">REEDOS</div>
                            <div className="reedos-eyebrow text-[9px]">Billing &amp; Commercial</div>
                        </div>
                    </div>

                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 mb-4"
                        data-testid="forgot-back-to-login"
                    >
                        <ArrowLeft size={12} weight="bold" /> Back to sign in
                    </Link>

                    {!sent ? (
                        <>
                            <div className="reedos-eyebrow mb-3">Account recovery</div>
                            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 leading-[1.05]">
                                Forgot your password?
                            </h1>
                            <p className="text-sm text-slate-500 mt-3">
                                Enter the email associated with your REEDOS account. If we find it, we'll send you a single-use link to set a new password. The link expires in 60 minutes.
                            </p>

                            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
                                <div>
                                    <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Registered email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@reedos.in"
                                        data-testid="forgot-email-input"
                                        className="mt-1.5 h-10 bg-white"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting || !email.trim()}
                                    data-testid="forgot-submit-btn"
                                    className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium gap-1.5"
                                >
                                    {submitting ? "Sending…" : "Send reset link"}
                                    {!submitting && <ArrowRight size={16} weight="bold" />}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div data-testid="forgot-success">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full mb-5">
                                <EnvelopeSimple size={22} weight="fill" className="text-emerald-700" />
                            </div>
                            <div className="reedos-eyebrow mb-3">Check your inbox</div>
                            <h1 className="font-display text-3xl tracking-tight font-bold text-slate-900 leading-[1.1]">
                                We've sent you a reset link.
                            </h1>
                            <p className="text-sm text-slate-500 mt-3">
                                If an account exists for <span className="font-mono text-slate-900">{email}</span>, you'll receive an email with a single-use link to set a new password. The link expires in 60 minutes.
                            </p>
                            <div className="rounded-md border border-slate-200 bg-white p-4 mt-6 text-[12px] space-y-2 text-slate-600">
                                <div className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /><span>Don't see it? Check your spam folder.</span></div>
                                <div className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /><span>The link can only be used once — after resetting, every active session will be signed out.</span></div>
                                <div className="flex gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /><span>If you didn't request this, simply ignore the email. Your password remains unchanged.</span></div>
                            </div>
                            <Button
                                onClick={() => { setSent(false); setEmail(""); }}
                                variant="outline"
                                className="mt-6 w-full h-10"
                                data-testid="forgot-resend-btn"
                            >
                                Send to a different email
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Right: branded panel */}
            <div className="hidden lg:flex bg-slate-900 text-white p-12 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage:
                        "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                }} />
                <div className="relative z-10 flex flex-col justify-between w-full">
                    <div>
                        <div className="reedos-eyebrow text-slate-400">Secure recovery</div>
                        <h2 className="font-display text-4xl tracking-tight font-bold mt-2 leading-[1.1]">
                            Account safety,<br />without the friction.
                        </h2>
                        <p className="text-slate-300 text-sm mt-4 max-w-md">
                            Self-service password recovery uses a single-use, time-bound link sent to your registered email. After a reset, every existing session is signed out automatically — no stale tokens, no lingering access.
                        </p>
                    </div>
                    <div className="text-[11px] text-slate-500 tracking-wide mt-10">
                        REEDOS · Billing &amp; Commercial Management System (BCMS)
                    </div>
                </div>
            </div>
        </div>
    );
}
